import { type DefaultRootState } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { type DatabaseDraft } from '@databases/utils/database/changesets';
import { createCachedSelector } from 're-reselect';

import {
  groupEntriesByField,
  type GroupsByKey,
  type EntriesByGroup
} from '../group-entries-by-field';
import { getGroupField, getSubgroupField } from '../grouping';
import { createReduxStateSelectors } from '../state-selectors/redux-state-selectors';

export type GroupsAndSubgroupsByKey = {
  groupsByKey?: GroupsByKey;
  subgroupsByKey?: GroupsByKey;
  entriesByGroup?: EntriesByGroup;
  entriesBySubgroup?: EntriesByGroup;
};

/**
 * Gets the full set of groups and subgroups for entries in the view.
 *
 * @returns An object containing:
 * - `groupsByKey`: Mapping of group keys to DatabaseGroup instance.
 * - `subgroupsByKey`: Mapping of subgroup keys to DatabaseGroup instance.
 * - `entriesByGroup`: Mapping of group keys to entry IDs belonging to each group.
 * - `entriesBySubgroup`: Mapping of subgroup keys to entry IDs belonging to each subgroup.
 *
 * If group or subgroup field isn't set, the respective properties will be undefined.
 */
export const selectGroupsAndSubgroupsByKey = createCachedSelector<
  DefaultRootState,
  { contentId: string; viewSettings: DatabaseViewSettings; viewId: string },
  DefaultRootState,
  DatabaseViewSettings,
  DatabaseDraft,
  DatabaseDraft,
  GroupsAndSubgroupsByKey
>(
  (state) => state,
  (state, { viewSettings }) => viewSettings,
  (state, { contentId }) =>
    selectDatabaseDraftByContentId(state, { contentId, includeDeleted: true }),
  (state, { viewSettings, contentId }) =>
    selectDatabaseDraftByContentId(state, {
      contentId,
      viewSettings,
      includeDeleted: true
    }),
  (
    state: DefaultRootState,
    viewSettings: DatabaseViewSettings,
    databaseDraft: DatabaseDraft,
    databaseDraftView: DatabaseDraft
  ) => {
    const groupField = getGroupField(databaseDraft, viewSettings);
    const subgroupField = getSubgroupField(databaseDraft, viewSettings);
    const stateSelectors = createReduxStateSelectors(state);
    const grouping = groupField
      ? groupEntriesByField(
          databaseDraft,
          databaseDraftView.entries,
          groupField,
          stateSelectors
        )
      : undefined;
    const subgrouping = subgroupField
      ? groupEntriesByField(
          databaseDraft,
          databaseDraftView.entries,
          subgroupField,
          stateSelectors
        )
      : undefined;

    return {
      groupsByKey: grouping?.groupsByKey,
      subgroupsByKey: subgrouping?.groupsByKey,
      entriesByGroup: grouping?.entriesByGroup,
      entriesBySubgroup: subgrouping?.entriesByGroup
    };
  }
)((state, { viewId }) => viewId);
